import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import dummyData from '@app/assets/api-dummy-data.json';
import { AuthService } from '@app/services/auth/auth.service';
import { CommonFunctionalitiesService } from '@app/services/common-functionalities.service';
import { ToastService } from '@app/services/toast.service';
import { TourGuideService } from '@app/services/tour-guide.service';
import { WfQueueManagerService } from '@app/services/wf-queue-manager.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { Priority, PriorityManager } from '@app/shared/components/que-mnger-expand-config/priority';
import { PriorityDialogComponent } from '@app/shared/components/que-mnger-expand-config/priority-dialog/priority-dialog.component';
import { QueDataSort as QueDataSorter } from '@app/shared/components/que-mnger-expand-config/que-mnger-data-sort';
import { ColumnManager } from '@app/util/column-manager.util';
import { ColumnOption } from '@app/model/column.model';
import { Messages as msg } from '@app/util/messages';
import { Messages } from '@app/util/messages.util';
import { TourGuideContent } from '@app/util/tour-guide-content.util';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { unAllowedJobsToAbort } from '@app/src/util/common-utils';
import { TitleService } from '@app/services/title.service';

export enum QueuedJobStatus {
  requested, queued, inprogress
}

export interface QueMgrData {
  workflowName: string;
  startTime: string;
  queueTime: number;
  status: QueuedJobStatus;
  estimatedTime: number;
  reasonForQue: string;
  priority: Priority
  jobId: any
}

@Component({
  selector: 'app-que-mnger-expand-config',
  templateUrl: './que-mnger-expand-config.component.html',
  styleUrls: ['./que-mnger-expand-config.component.scss']
})
export class QueMngerExpandConfigComponent implements OnInit {

  /****************** Variable declaration ******************/
  sorter = new QueDataSorter();
  abortNotAllowed = Messages.qMngrExpandCfg.abortWorkflowJob.abortNotAllowed;
  currentPage: number;
  tourId = TourGuideContent
  _dataSource = new MatTableDataSource<QueMgrData>();
  prioMgr = new PriorityManager();
  dataSubscription: Subscription;
  dynamicWidth: number = 180; // inital width of a column
  openInNewIcon = this.titleService.openInNewIcon;
  private subscription: Subscription;


  colMgr = new ColumnManager([
    { header: 'Repository name', key: 'repoName', style: { all: { 'justify-content': 'left' } } },
    { header: 'Event name', key: 'event' },
    { header: 'User', key: 'triggeredBy', style: { all: { 'justify-content': 'left' } } },
    { header: 'Start time', key: 'startTime', style: { all: { 'justify-content': 'left' } } },
    { header: 'Queued time', key: 'queueTime', style: { all: { 'justify-content': 'right' } } },
    { header: 'Status', key: 'status' },
    { header: 'Estimated time', key: 'estimatedTime', style: { all: { 'justify-content': 'right' } } },
    { header: 'Reason for queue', key: 'reasonForQue' },
    { header: 'Priority', key: 'priority' },
    { header: 'Action', key: 'jobId', sortable: false }
  ]);
  /****************** View child ******************/
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator

  /****************** Input and Output decorators ******************/
  @Input() duration: any;
  @Input() wfId: any = 0;
  @Input() columns?: ColumnOption[];
  @Input() pageSize = 5;
  @Input() pageSizeOptions: number[] = [5];
  @Input() pageIndex = 0;
  @Input() dynamicActionLoadingJobId: string | null = null;
  @Input() cancellingJobId: string | null = null;
  @Output() cancelRunClick = new EventEmitter<any>();
  @Input() refreshTooltip = 'Refresh';
  @Input() redispatchTooltip = 'Redispatch';
  @Output() _data = new EventEmitter<any[]>();
  @Output() refreshClickEvent = new EventEmitter<void>();
  @Output() dynamicActionClick = new EventEmitter<any>();
  @Output() customPageChange = new EventEmitter<PageEvent>();


  /**
   * Constructor
   * @param wfQueueService 
   * @param cf 
   * @param dialog 
   * @param toast 
   * @param tourGuideService 
   * @param authService 
   * @param cf
   * @param titleService
   */
  constructor(
    public wfQueueService: WfQueueManagerService,
    public cf: CommonFunctionalitiesService,
    private dialog: MatDialog,
    private toast: ToastService,
    public tourGuideService: TourGuideService,
    public authService: AuthService,
    public titleService: TitleService,
    public commanService: CommonFunctionalitiesService,
  ) {
  }
  /**
   * setter for dataSource
   * @param dataSource dataSource
   */
  @Input('dataSource') set dataSource(dataSource: any[]) {
    this._dataSource.data = dataSource.slice();
    this.sorter.dataSource = this._dataSource.data;
    this.sorter.sort();
    if (this.hasCustomColumns && this.paginator) {
      const lastPageIndex = Math.max(0, Math.ceil(this._dataSource.data.length / this.pageSize) - 1);
      this.pageIndex = Math.min(this.pageIndex, lastPageIndex);
      this.currentPage = this.pageIndex;
      this.paginator.pageIndex = this.pageIndex;
    }
  }

  /**
   * setter for dataSource
   * @returns dataSource
   */
  get dataSource() {
    return this._dataSource.data;
  }

  get hasCustomColumns(): boolean {
    return !!this.columns;
  }

  /**ngOnInit */
  ngOnInit() {
    this._dataSource.paginator = this.paginator;
    if (this.hasCustomColumns) {
      this.colMgr = new ColumnManager(this.columns!);
      return;
    }
    this.currentPage = this.wfQueueService.getCurrentPage();

    if (this.tourGuideService.isStartTour) {
      this.dataSource = dummyData.wfQueueMgr.queMgrTableDetails
      return;
    }
    this.getQueuedData();
    if (!this.wfQueueService.widthFlag) {
      this.wfQueueService.maxWidth = 160;
    }
  }

  /**ngOnChanges */
  ngOnChanges() {
    if (this.hasCustomColumns) return;
    if (this.wfQueueService.sortedData) {
      this.dataSource = this.wfQueueService.sortedData;
    }
    this.subscription = this.commanService.dataSubject.subscribe((object) => {
      this.wfQueueService.maxWidth = object.width;
    });
  }

  /**ngOnDestroy */
  ngOnDestroy() {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
  }

  /**
   * Retrieves queued data
   */
  getQueuedData() {
    this.wfQueueService.getQueueWfDataForConfig(this.duration?.value, this.wfId).subscribe(({ data: { dbResp: resp } }) => {
      resp.forEach((ds: any) => ds.startTime = this.cf.getGMTtimeFormat(this.cf.formatTimestamp(ds.startTime)));
      this.dataSource = resp;
      this._data.emit(this.dataSource)
    });
  }

  /** onPageChange function is triggered when we change the table pagination */
  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    if (this.hasCustomColumns) {
      this.customPageChange.emit(event);
      return;
    }
    this.wfQueueService.setCurrentPage(event.pageIndex);
  }

  refreshDynamicExecutionDetails(): void {
    this.refreshClickEvent.emit();
  }

  isDynamicTriggerAllowed(status: string, conclusion?: string | null): boolean {
    return ['requested', 'queued'].includes(status)
    // TODO: enable to allow re-dispatch on aborted jobs with github_abort conclusion
    // || (status === 'abort' && conclusion === 'github_abort');
  }

  triggerDynamicAction(detail: any): void {
    if (!this.dynamicActionLoadingJobId && this.isDynamicTriggerAllowed(detail.status, detail.conclusion)) {
      this.dynamicActionClick.emit(detail);
    }
  }

  /**
   * Sorts the data
   * @param sort
   */
  sortData(sort: Sort) {
    if (this.hasCustomColumns) {
      if (!sort.direction) return;
      this.dataSource = [...this.dataSource].sort((first, second) => {
        const comparison = String(first[sort.active] ?? '').localeCompare(String(second[sort.active] ?? ''));
        return sort.direction === 'asc' ? comparison : -comparison;
      });
      return;
    }
    if (sort.direction == '') {
      sort.active = 'startTime'
      sort.direction = 'desc'
    }
    this.sorter.column = sort.active;
    this.sorter.asc = sort.direction === "asc";
    this.dataSource = this.sorter.sort();
    this.wfQueueService.setSortedData(this.dataSource);
  }

  /**getDuration */
  getDuration(timeInSec: number) {
    return this.cf.getDurationFromSeconds(timeInSec);
  }

  /**changePriority of the job */
  changePriority(data: any, element: any) {
    //Check for access
    if (!this.authService.permissions['set-job-priority']) return;

    this.dialog.open(PriorityDialogComponent, {
      data: Priority[data.priority],
      autoFocus: false
    }).afterClosed().subscribe((resp: Priority) => {
      if (resp) {
        this.updatePriority(data.jobId, resp).then(() => {
          element.dispatchEvent(new CustomEvent('onChange', {
            detail: {
              class: {
                previous: this.priorityClass(data.priority),
                current: this.priorityClass(resp)
              }
            }
          }));

          data.priority = resp
          this.dataSource.splice(this.dataSource.indexOf(data), 1, data);
          this.dataSource = this.dataSource.slice();
        }).catch();
      }
    });
  }

  /**
   * updates the priority of the workflow job in the database
   * @param jobId workflow jobId
   * @param priority high, medium or low
   * @returns Promise
   */
  updatePriority(jobId: any, priority: Priority): Promise<void> {
    return new Promise((resolve, reject) => {
      this.wfQueueService.updatePriority(jobId, priority).subscribe(() => {
        this.toast.showSuccess(Messages.qMngrExpandCfg.successMsg, Messages.qMngrExpandCfg.priority.updated);
        resolve();
      }, () => {
        this.toast.showError(Messages.qMngrExpandCfg.errorMsg, Messages.qMngrExpandCfg.priority.updateFailed);
        reject();
      })
    })
  }

  /**
   * Shows the priority whether it is
   * High, medium or low
   * @param priority
   * @returns
   */
  priorityClass(priority: any) {
    switch (priority) {
      case 'high':
        return Messages.qMngrExpandCfg.priority.high;
      case 'medium':
        return Messages.qMngrExpandCfg.priority.medium;
      case 'low':
        return Messages.qMngrExpandCfg.priority.low;
      default:
        return Messages.qMngrExpandCfg.priority.default;
    }
  }

  /**
   * Shows the status whether it is
   * Queued or Inprogress
   * @param status
   * @returns
   */
  statusClass(status: any) {
    switch (status) {
      case 'queued':
      case 'abort':
      case 'stopped':
        return Messages.qMngrExpandCfg.status.queued;

      case 'inprogress':
        return Messages.qMngrExpandCfg.status.inprogress;

      case 'success':
      case 'complete':
        return 'alert-success';

      case 'failed':
      case 'cancelled':
        return 'alert-danger';

      case 'skipped':
      case 'system_abort':
      case 'user_abort':
      case 'github_abort':
        return 'alert-secondary';

      case 'requested':
      case 'dispatched':
        return Messages.qMngrExpandCfg.status.dispatched;

      default:
        return Messages.qMngrExpandCfg.status.default;
    }
  }

  isAbortAllowed(status: string): boolean {
    return !unAllowedJobsToAbort.includes(status)
  }

  /**onAbort workflow */
  onAbort(data: any) {
    this.dialog.open(ConfirmDialogComponent, {
      width: "370px",
      disableClose: true,
      data: new ConfirmDialogModel(msg.areYouSure, msg.abortJobSurety)
    }).afterClosed().subscribe((dialogResult: boolean) => {
      if (dialogResult) {
        this.wfQueueService.abortWorkflowJob(data.jobId, data.status, data.actionUrl).subscribe({
          next: () => {
            this.dataSource.splice(this.dataSource.indexOf(data), 1);
            this.dataSource = this.dataSource.filter(item => item !== data);
            const wfId = [...new Set(this.dataSource.map(item => item.configId))];
            this.wfQueueService.updateDataSourceLength(wfId[0], this.dataSource.length);
            this.toast.showSuccess(Messages.qMngrExpandCfg.successMsg, Messages.qMngrExpandCfg.abortWorkflowJob.aborted);
          },
          error: () => {
            this.toast.showError(Messages.qMngrExpandCfg.errorMsg, Messages.qMngrExpandCfg.abortWorkflowJob.abortFailed);
          }
        })
      }
    });
  }
}